import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchMasterSyllabus,
  publishMasterSyllabus as publishMasterSyllabusApi,
  unpublishMasterSyllabus as unpublishMasterSyllabusApi,
} from "../service/syllabusService";
import type { MasterSyllabus, MasterSyllabusScope, SyllabusPublishScope } from "../types";
import i18n from "@/i18n";

function scopeKey(scope: MasterSyllabusScope) {
  return `${scope.academicGroupId}-${scope.academicYearId}`;
}

interface SyllabusState {
  masterByScope: Record<string, MasterSyllabus>;
  loading: boolean;
  error: string | null;
}

const initialState: SyllabusState = {
  masterByScope: {},
  loading: false,
  error: null,
};

export const loadMasterSyllabus = createAsyncThunk(
  "syllabus/loadMaster",
  async ({ institutionId, scope }: { institutionId: number; scope: MasterSyllabusScope }, { rejectWithValue }) => {
    try {
      const master = await fetchMasterSyllabus(institutionId, scope);
      return { key: scopeKey(scope), master };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? i18n.t("syllabus:syllabusSlicetsMsgs.failedToLoadMasterSyllabus"));
    }
  },
  {
    // Avoid refetching a scope that's already loading — a page mount re-firing this while
    // the first request is still in flight would otherwise race and double-fetch.
    condition: (_, { getState }) => {
      const state = getState() as { syllabus: SyllabusState };
      return !state.syllabus.loading;
    },
  }
);

export const publishMasterSyllabus = createAsyncThunk(
  "syllabus/publishMaster",
  async ({ institutionId, scope }: { institutionId: number; scope: SyllabusPublishScope }, { rejectWithValue }) => {
    try {
      const master = await publishMasterSyllabusApi(institutionId, scope);
      return { key: scopeKey(scope), master };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? i18n.t("syllabus:syllabusSlicetsMsgs.failedToPublishMasterSyllabus"));
    }
  }
);

export const unpublishMasterSyllabus = createAsyncThunk(
  "syllabus/unpublishMaster",
  async ({ institutionId, scope }: { institutionId: number; scope: SyllabusPublishScope }, { rejectWithValue }) => {
    try {
      const master = await unpublishMasterSyllabusApi(institutionId, scope);
      return { key: scopeKey(scope), master };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? i18n.t("syllabus:syllabusSlicetsMsgs.failedToUnpublishMasterSyllabus"));
    }
  }
);

const syllabusSlice = createSlice({
  name: "syllabus",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadMasterSyllabus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadMasterSyllabus.fulfilled, (state, action) => {
        state.loading = false;
        state.masterByScope[action.payload.key] = action.payload.master;
      })
      .addCase(loadMasterSyllabus.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) ?? i18n.t("syllabus:syllabusSlicetsMsgs.failedToLoadMasterSyllabus");
      })
      .addCase(publishMasterSyllabus.fulfilled, (state, action) => {
        state.masterByScope[action.payload.key] = action.payload.master;
      })
      .addCase(publishMasterSyllabus.rejected, (state, action) => {
        state.error = (action.payload as string) ?? i18n.t("syllabus:syllabusSlicetsMsgs.failedToPublishMasterSyllabus");
      })
      .addCase(unpublishMasterSyllabus.fulfilled, (state, action) => {
        state.masterByScope[action.payload.key] = action.payload.master;
      })
      .addCase(unpublishMasterSyllabus.rejected, (state, action) => {
        state.error = (action.payload as string) ?? i18n.t("syllabus:syllabusSlicetsMsgs.failedToUnpublishMasterSyllabus");
      });
  },
});

export { scopeKey as syllabusScopeKey };
export default syllabusSlice.reducer;
